import React, { useEffect, useState } from "react";
import { ShieldOff, Mail, Phone, Clock, AlertOctagon, LogOut, History } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch { return iso; }
}

/**
 * Full-screen "Account Locked" takeover for candidates.
 * Red + white glass-morphism, non-dismissible, shows exact reason, previous
 * lock count, complete lock history and HR contact info. Never displays
 * internal thresholds or detection logic.
 */
export default function AccountLockedScreen({ fallbackReason = "" }) {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/candidate/lock-info");
        if (alive) setInfo(r.data);
      } catch (e) { if (alive) setErr(true); }
    })();
    return () => { alive = false; };
  }, []);

  const hr = info?.hr || {};
  const history = info?.history || [];
  const previous = info?.previous_lock_count ?? 0;
  const reason = info?.lock_reason || fallbackReason || "Your account has been locked due to a violation of Pelico Data Work Policies.";
  const lockedAt = info?.locked_at;
  const humanReason = history.find((h) => h.event_type === "lock")?.reason_human;

  const onLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div
      data-testid="account-locked-screen"
      className="fixed inset-0 z-[100] overflow-y-auto"
      style={{
        background:
          "radial-gradient(1200px 600px at 10% -10%, rgba(225,29,72,0.35), transparent 60%)," +
          "radial-gradient(900px 500px at 110% 110%, rgba(190,18,60,0.35), transparent 60%)," +
          "linear-gradient(135deg, #7f1d1d 0%, #b91c1c 45%, #e11d48 100%)",
      }}
    >
      {/* Grain / noise overlay */}
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.12]"
        style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")" }}
      />

      <div className="relative min-h-screen flex items-start sm:items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-4xl">
          {/* Header banner */}
          <div className="flex items-center justify-between mb-4">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white text-xs font-semibold tracking-widest uppercase">
              <AlertOctagon size={14} /> Restricted Access
            </div>
            <button
              data-testid="locked-logout-btn"
              onClick={onLogout}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white text-xs font-semibold hover:bg-white/25"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>

          {/* Main glass card */}
          <div
            className="rounded-3xl border border-white/30 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)] overflow-hidden"
            style={{
              background: "rgba(255,255,255,0.92)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
            }}
          >
            <div className="p-6 sm:p-10">
              <div className="flex items-start gap-5">
                <div className="shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-rose-600 text-white flex items-center justify-center shadow-[0_10px_30px_-8px_rgba(225,29,72,0.6)]">
                  <ShieldOff size={36} strokeWidth={2.2} />
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-rose-600">Account Locked</div>
                  <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-zinc-900 mt-1 leading-tight">
                    Your assessment account has been suspended.
                  </h1>
                  <p className="text-sm sm:text-base text-zinc-700 mt-3 leading-relaxed max-w-2xl">
                    Access to the typing assessment has been restricted due to a violation of Pelico Data Work Policies.
                    Please review the details below and contact HR for further assistance.
                  </p>
                </div>
              </div>

              {/* Reason + locked-at grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-8">
                <div data-testid="locked-reason-card" className="md:col-span-2 rounded-2xl border border-rose-200 bg-rose-50/70 p-5">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-rose-600 font-bold">Reason for lock</div>
                  <div className="text-lg font-semibold text-zinc-900 mt-2">{reason}</div>
                  {humanReason && humanReason !== reason && (
                    <div className="text-sm text-zinc-700 mt-2">{humanReason}</div>
                  )}
                </div>
                <div className="rounded-2xl border border-zinc-200 bg-white p-5">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Locked at</div>
                  <div className="text-sm font-semibold text-zinc-900 mt-2 flex items-center gap-2">
                    <Clock size={14} className="text-rose-600" />
                    <span data-testid="locked-at-value">{fmtDateTime(lockedAt)}</span>
                  </div>
                  <div className="mt-4 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Previous locks on this account</div>
                  <div data-testid="locked-previous-count" className="text-2xl font-bold text-zinc-900 mt-1">
                    {previous}
                  </div>
                </div>
              </div>

              {/* HR contact */}
              <div className="mt-6 rounded-2xl border border-zinc-200 bg-white p-5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Contact HR</div>
                <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                  {hr.email && (
                    <a
                      data-testid="locked-hr-email"
                      href={`mailto:${hr.email}?subject=Account%20Locked%20-%20Assistance%20Required`}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700"
                    >
                      <Mail size={16} /> {hr.email}
                    </a>
                  )}
                  {hr.phone && (
                    <a
                      data-testid="locked-hr-phone"
                      href={`tel:${hr.phone}`}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-zinc-300 text-zinc-900 text-sm font-semibold hover:bg-zinc-50"
                    >
                      <Phone size={16} /> {hr.phone}
                    </a>
                  )}
                  {!hr.email && !hr.phone && (
                    <div className="text-sm text-zinc-500">Please reach out to your administrator for assistance.</div>
                  )}
                </div>
              </div>

              {/* Lock / unlock history */}
              <div className="mt-6">
                <div className="flex items-center gap-2 text-zinc-900 font-semibold">
                  <History size={16} className="text-rose-600" />
                  <span>Lock &amp; Unlock History</span>
                  <span className="text-xs text-zinc-500 font-normal">({history.length} events)</span>
                </div>
                <div className="mt-3 rounded-2xl border border-zinc-200 overflow-hidden">
                  {history.length === 0 && (
                    <div data-testid="locked-history-empty" className="p-5 text-sm text-zinc-500 bg-white">
                      {err ? "Unable to load history." : "No previous events recorded."}
                    </div>
                  )}
                  {history.length > 0 && (
                    <ul data-testid="locked-history-list" className="divide-y divide-zinc-100 bg-white">
                      {history.map((h) => {
                        const isLock = h.event_type === "lock";
                        return (
                          <li key={h.id} className="px-4 py-3 flex items-start gap-3">
                            <div className={`mt-0.5 w-2 h-2 rounded-full ${isLock ? "bg-rose-600" : "bg-emerald-500"}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-[10px] uppercase tracking-widest font-bold ${isLock ? "text-rose-600" : "text-emerald-600"}`}>
                                  {isLock ? "Locked" : "Unlocked by admin"}
                                </span>
                                <span className="text-xs text-zinc-500">{fmtDateTime(h.occurred_at)}</span>
                                {h.page_number != null && (
                                  <span className="text-[10px] uppercase tracking-widest text-zinc-400">Page {h.page_number}</span>
                                )}
                              </div>
                              {isLock && h.reason_human && (
                                <div className="text-sm text-zinc-800 mt-1">{h.reason_human}</div>
                              )}
                              {!isLock && h.admin_email && (
                                <div className="text-sm text-zinc-700 mt-1">By {h.admin_email}</div>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>

              <div className="mt-8 text-[11px] text-zinc-500 leading-relaxed">
                This restriction is logged and cannot be dismissed from this device. Attempting to bypass the lock is a further violation of Pelico Data Work Policies.
              </div>
            </div>
          </div>

          <div className="text-center text-white/70 text-xs mt-6">
            © {new Date().getFullYear()} Pelico Data · Assessment Integrity System
          </div>
        </div>
      </div>
    </div>
  );
}
